import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Sparkles, RotateCcw, ArrowLeft, ChevronRight, MessageCircle, Send, Loader2, CheckCircle2, Info, Layers } from "lucide-react";
import { callImagenWithRetry } from "./imagenClient";
import { callAnthropicMessages } from "./anthropicClient";

const C = "#2D2926";

const FABRIC_META = {
  "brocade":        { name:"Brocade",          drape:"structured, holds its own shape" },
  "chiffon":        { name:"Chiffon",          drape:"fluid, responds to air and movement" },
  "crepe":          { name:"Crepe",            drape:"semi-fluid, controlled and restrained" },
  "georgette":      { name:"Georgette",        drape:"fluid with gentle texture" },
  "jacquard":       { name:"Jacquard",         drape:"structured with intrinsic pattern" },
  "lace":           { name:"Lace",             drape:"semi-structured, romantic and detailed" },
  "linen":          { name:"Linen",            drape:"semi-structured, relaxed and honest" },
  "organza":        { name:"Organza",          drape:"structured sheer, sculptural volume" },
  "satin":          { name:"Satin",            drape:"semi-fluid, reflective and transforming" },
  "silk":           { name:"Silk",             drape:"fluid to semi-structured, luminous" },
  "velvet":         { name:"Velvet",           drape:"fluid-heavy, absorbs and hoards light" },
  "wool":           { name:"Wool",             drape:"structured, responds to the tailor" },
  "deadstock-silk": { name:"Deadstock Silk",   drape:"fluid to semi-structured, storied" },
  "organic-cotton": { name:"Organic Cotton",   drape:"semi-structured, honest and clean" },
  "tencel":         { name:"Tencel / Lyocell", drape:"fluid, thoughtful and refined" },
  "custom":         { name:"Custom Fabric",    drape:"as specified" },
};

const GOAL_ATMOSPHERE = {
  "business": "A high-ceiling boardroom in a glass skyscraper overlooking a foggy morning metropolis",
  "cocktail": "An upscale rooftop lounge at golden hour with soft amber city lights",
  "gala":     "A grand marble ballroom with crystal chandeliers and live orchestral music",
  "bridal":   "A historic stone cathedral with soft diffused natural light",
};


function mergeFormWithDesignSpec(form, designSpec) {
  if (!designSpec) return form || {};
  return {
    ...(form || {}),
    category: designSpec.category ?? form?.category,
    fabricId: designSpec.fabricId ?? form?.fabricId,
    colorDescription: designSpec.colorDescription ?? form?.colorDescription,
    colorHex: designSpec.colorHex ?? form?.colorHex,
    lining: designSpec.lining ?? form?.lining,
    separates: typeof designSpec.separates === "boolean" ? designSpec.separates : form?.separates,
    cut: designSpec.cut ?? form?.cut,
    neckline: designSpec.neckline ?? form?.neckline,
    sleeves: designSpec.sleeves ?? form?.sleeves,
    dressLength: designSpec.dressLength ?? form?.dressLength,
    frontDescription: designSpec.frontDescription ?? form?.frontDescription,
    backDescription: designSpec.backDescription ?? form?.backDescription,
    skirtType: designSpec.skirtType ?? form?.skirtType,
    skirtLength: designSpec.skirtLength ?? form?.skirtLength,
    jacketNeckline: designSpec.jacketNeckline ?? form?.jacketNeckline,
    jacketSleeves: designSpec.jacketSleeves ?? form?.jacketSleeves,
    jacketFront: designSpec.jacketFront ?? form?.jacketFront,
    jacketBack: designSpec.jacketBack ?? form?.jacketBack,
    skirtFront: designSpec.skirtFront ?? form?.skirtFront,
    skirtBack: designSpec.skirtBack ?? form?.skirtBack,
    atmosphere: designSpec.atmosphere ?? form?.atmosphere,
    designDetails: designSpec.designDetails ?? form?.designDetails ?? {},
    designDetailsSummary: designSpec.designDetailsSummary ?? form?.designDetailsSummary ?? '',
    backNeckline: designSpec.backNeckline ?? form?.backNeckline ?? 'Low V-Back',
    closure: designSpec.closure ?? form?.closure ?? 'Invisible Zip — Centre Back',
  };
}

const PROMPT_CHIPS = [
  { label:"The silhouette",       message:"Can you tell me more about how this silhouette works in this fabric?" },
  { label:"Construction details", message:"What construction details would make this design most wearable and well-finished?" },
  { label:"Fabric or colour",     message:"I am open to fabric or colour suggestions — what would work well with this design?" },
  { label:"Interior finishing",   message:"What interior finishing techniques would you recommend for this garment?" },
];

function analyseProportions(m, silhouette, fabricId) {
  const obs       = [];
  const bust      = parseFloat(m.bust)        || 0;
  const highBust  = parseFloat(m.highBust)    || 0;
  const waist     = parseFloat(m.waist)       || 0;
  const hips      = parseFloat(m.hips)        || 0;
  const height    = parseFloat(m.height)      || 0;
  const hollow    = parseFloat(m.hollowToHem) || 0;
  const shoulder  = parseFloat(m.shoulder)    || 0;
  const w2h       = parseFloat(m.waistToHip)  || 0;
  const bustDiff  = bust - highBust;
  const hipDrop   = hips - waist;

  if (waist && hips) {
    if (hipDrop >= 10) {
      obs.push({
        signal: "Waist definition",
        observation: "A pronounced waist-to-hip relationship — one of the most beautiful proportions to dress.",
        technique: "A wrap silhouette or bias cut will follow this naturally. Princess seaming or a French dart will sculpt it from within.",
        severity: "positive",
      });
    } else if (hipDrop >= 7) {
      obs.push({
        signal: "Waist definition",
        observation: "A classic, balanced waist-to-hip proportion.",
        technique: "Standard seaming works beautifully here. Side-seam ruching adds softness if desired.",
        severity: "neutral",
      });
    } else {
      obs.push({
        signal: "Waist definition",
        observation: "A straighter, more column-like torso — wonderful for clean, architectural silhouettes.",
        technique: "A wrap front or strategically placed waist darts create definition without imposing it. Bias cut follows the natural line with grace.",
        severity: "neutral",
      });
    }
  }

  if (bust && highBust) {
    if (bustDiff >= 3) {
      obs.push({
        signal: "Bust structure",
        observation: "Meaningful difference between high bust and full bust — the bodice needs to account for this precisely.",
        technique: "Built-in support or a boned bodice is recommended. A cowl drape at the front skims beautifully and avoids pulling.",
        severity: "advisory",
      });
    } else if (bustDiff >= 1.5) {
      obs.push({
        signal: "Bust structure",
        observation: "Moderate bust projection — standard bodice construction handles this well.",
        technique: "A French dart from hip to bust gives clean shaping without interrupting the surface. Consider a soft cowl for fluid fabrics.",
        severity: "neutral",
      });
    }
  }

  if (shoulder && hips) {
    const shoulderSpan = shoulder * 2;
    const ratio = shoulderSpan / hips;
    if (ratio > 0.95) {
      obs.push({
        signal: "Shoulder balance",
        observation: "Broad shoulders relative to hip — a powerful, striking proportion.",
        technique: "A V-neck or cowl drape draws the eye inward and down. A bias-cut skirt with gentle flare balances beautifully.",
        severity: "advisory",
      });
    } else if (ratio < 0.78) {
      obs.push({
        signal: "Shoulder balance",
        observation: "Narrower shoulders relative to hip — creates a lovely, feminine silhouette.",
        technique: "Boat neck or off-shoulder details add visual width at the shoulder. A wrap bodice with fitted skirt works particularly beautifully here.",
        severity: "advisory",
      });
    }
  }

  if (height && hollow) {
    const ratio = hollow / height;
    if (ratio < 0.62) {
      obs.push({
        signal: "Vertical line",
        observation: "The chosen hem length creates a shorter visual line — worth considering whether the silhouette elongates or grounds.",
        technique: "A vertical seam line, empire waist, or floor-length hem will lengthen. V-neckline adds vertical emphasis.",
        severity: "advisory",
      });
    } else if (ratio > 0.78) {
      obs.push({
        signal: "Vertical line",
        observation: "A long hem relative to height — creates a dramatic, elongated line.",
        technique: "This proportion suits a bias cut or column silhouette particularly well. Consider a subtle side slit for movement.",
        severity: "positive",
      });
    }
  }

  if (w2h) {
    if (w2h >= 10) {
      obs.push({
        signal: "Skirt volume",
        observation: "A long waist-to-hip drop suggests a generous hip curve — the skirt needs to clear it cleanly.",
        technique: "A wrap or bias cut follows the curve more elegantly than a standard A-line. Avoid pencil skirts without significant ease.",
        severity: "advisory",
      });
    } else if (w2h <= 7) {
      obs.push({
        signal: "Skirt volume",
        observation: "A shorter rise from waist to hip — the skirt transitions quickly.",
        technique: "A higher waistband or empire line visually extends this proportion. Ruching at the hip adds softness where the transition is abrupt.",
        severity: "neutral",
      });
    }
  }

  const fluidFabrics = ["chiffon", "georgette", "silk", "tencel", "deadstock-silk", "crepe"];
  const stiffSilhouettes = ["Ball Gown", "Trumpet", "Fit-and-Flare"];
  if (fluidFabrics.includes(fabricId) && stiffSilhouettes.includes(silhouette)) {
    const fabricName = (FABRIC_META[fabricId] || {}).name || "This fabric";
    obs.push({
      signal: "Fabric and silhouette",
      observation: fabricName + " is fluid by nature — " + silhouette + " silhouettes typically need structure to hold their shape.",
      technique: "An underlayer or boning will be needed to support the silhouette. Alternatively, a bias cut or wrap works with the fabric rather than against it.",
      severity: "advisory",
    });
  }

  return obs;
}

const DRESS_LENGTH_HEM_LOGIC = {
  "Mini":       "mini length, well above the knee",
  "Above-knee": "above-knee length",
  "Knee":       "knee-length",
  "Midi":       "midi length, mid-calf",
  "Tea":        "tea length, lower-calf",
  "Ankle":      "ankle-length",
  "Floor":      "floor-length",
};

// ─────────────────────────────────────────────────────────────────────────────
// Measurement to visual figure descriptor
// Uses photographic/casting language that image models respond to.
// Neutral, affirming — no body type judgements or size labels.
// ─────────────────────────────────────────────────────────────────────────────
function buildFigureDescriptor(measurements) {
  const height   = parseFloat(measurements.height)      || 0;
  const bust     = parseFloat(measurements.bust)        || 0;
  const waist    = parseFloat(measurements.waist)       || 0;
  const hips     = parseFloat(measurements.hips)        || 0;
  const shoulder = parseFloat(measurements.shoulder)    || 0;

  // Always anchor as adult woman — prevents model misinterpreting petite/full descriptors
  const parts = ["adult woman"];

  // ── Height ────────────────────────────────────────────────────────────────
  if (height > 0) {
    if (height < 63)       parts.push("petite height, under 5ft 3in");
    else if (height <= 66) parts.push("average height, around 5ft 5in");
    else if (height <= 70) parts.push("tall, around 5ft 8in");
    else                   parts.push("very tall, above 5ft 10in");
  }

  // ── Overall frame — derived from bust as a size anchor ───────────────────
  // Imagen responds to casting-style body frame language.
  // Ranges calibrated to RTW sizing: missy (bust 32–42"), plus (43–58").
  if (bust > 0) {
    if (bust <= 36)        parts.push("slender frame");
    else if (bust <= 42)   parts.push("medium frame");
    else if (bust <= 48)   parts.push("full figure, generously proportioned");
    else                   parts.push("plus-size figure, full and rounded silhouette");
  }

  // ── Waist definition — visual language ───────────────────────────────────
  if (bust > 0 && waist > 0) {
    const bwDiff = bust - waist;
    if (bwDiff >= 10)      parts.push("visibly defined waist");
    else if (bwDiff >= 5)  parts.push("gently curved waist");
    else                   parts.push("straight torso, minimal waist curve");
  }

  // ── Hip silhouette — visual language ─────────────────────────────────────
  if (hips > 0 && waist > 0) {
    const hwDiff = hips - waist;
    if (hwDiff >= 12)      parts.push("full rounded hips, clearly wider than waist");
    else if (hwDiff >= 6)  parts.push("gently curved hips");
    else                   parts.push("straight hip line, similar width to waist");
  }

  // ── Shoulder width relative to hips ──────────────────────────────────────
  if (shoulder > 0 && hips > 0) {
    const ratio = (shoulder * 2) / hips;
    if (ratio > 0.95)      parts.push("broad shoulders");
    else if (ratio < 0.78) parts.push("narrow shoulders relative to hip");
  }

  return parts.join(", ");
}


// Imagen vocabulary for structural design details (mirrors Studio)
const SKIRT_BEHAVIOR_IMAGEN_DESC = {
  "Column fall":      "narrow straight column skirt with no flare — skirt falls in a slim straight line from waist to hem, no A-line shape, no volume",
  "Bias-cut skirt":   "bias-cut skirt, fabric cut on the diagonal, flowing and clinging gently to the body",
  "Gentle flare":     "skirt with gentle A-line flare, softly widening from hip to hem",
  "Soft gathers":     "softly gathered skirt with relaxed fullness at the waist",
  "Knife pleats":     "knife-pleated skirt, sharp uniform pleats falling from the waistband",
  "Box pleats":       "box-pleated skirt, structured pleats creating controlled volume",
  "Draped skirt":     "softly draped skirt with flowing fabric folds",
  "Directional drape":"directionally draped skirt with asymmetric fabric flow",
};
const WAIST_IMAGEN_DESC = {
  "Defined waist seam": "with a clean defined waist seam at the natural waist",
  "Dropped waist":    "with a dropped waistline — the waist seam sits below the natural waist at the hip",
  "Basque waist":     "with a basque waistband sitting below the natural waist",
  "Gathered waist":   "with soft gathers at the waistline",
  "Pleated waist":    "with a pleated waistline",
  "Draped waist":     "with soft fabric draped at the waist",
};
const BODICE_IMAGEN_DESC = {
  "Structured bodice":  "structured bodice with internal support, holding its shape",
  "Bias-cut bodice":    "bias-cut bodice, fabric cut on the diagonal for a soft clinging fit",
  "Darted bodice":      "dart-fitted bodice with visible shaping darts",
  "Princess seams":     "princess-seamed bodice with vertical seaming from shoulder to waist",
  "Corset-style seaming": "bodice with corset-style boning seams and structured construction",
  "Wrapped bodice":     "wrapped bodice, fabric crossing over the front",
  "Draped bodice":      "softly draped bodice with flowing fabric folds across the front",
  "Ruched bodice":      "ruched bodice with gathered fabric creating texture",
  "Soft gathering":     "bodice with soft gathers",
};

function buildSalonCompositeGarmentDesc(source, fabricName, finalCut, finalNeck, finalSlv) {
  const d = source.designDetails || {};
  const skirtOverride  = d.skirtBehavior && d.skirtBehavior !== "Clean fall"
    ? SKIRT_BEHAVIOR_IMAGEN_DESC[d.skirtBehavior] : null;
  const waistOverride  = d.waist && d.waist !== "Clean waist"
    ? WAIST_IMAGEN_DESC[d.waist] : null;
  const bodiceOverride = d.bodice && d.bodice !== "Clean"
    ? BODICE_IMAGEN_DESC[d.bodice] : null;
  const skirtFeature   = d.skirtFeature && d.skirtFeature !== "None"
    ? d.skirtFeature.toLowerCase() : null;

  let shapeDesc;
  if (skirtOverride) {
    const bodiceContext = finalCut === "A-line" || finalCut === "Ball Gown"
      ? "fitted bodice"
      : finalCut === "Mermaid" || finalCut === "Trumpet"
        ? "closely fitted bodice and hips"
        : "fitted bodice";
    shapeDesc = `${bodiceContext} with ${skirtOverride}`;
  } else {
    shapeDesc = `${finalCut} silhouette`;
  }

  const waistDesc   = waistOverride  ? `, ${waistOverride}` : "";
  const bodiceDesc  = bodiceOverride ? `, ${bodiceOverride}` : "";
  const featureDesc = skirtFeature   ? ` with ${skirtFeature}` : "";

  if (source.separates) {
    return `${fabricName} tailored jacket with ${source.jacketNeckline} neckline and ${source.jacketSleeves} sleeves, paired with a ${source.skirtType} ${String(source.skirtLength || "midi").toLowerCase()} skirt`;
  }
  return `${fabricName} dress${bodiceDesc}${waistDesc}, ${shapeDesc}${featureDesc}, with ${finalNeck} front neckline, ${source.backNeckline || "low back"} back neckline, and ${finalSlv} sleeves at ${String(source.dressLength || "midi").toLowerCase()} length`;
}

function buildSalonPrompts(form, measurements, designSpec, skinTone) {
  const source     = mergeFormWithDesignSpec(form, designSpec);
  const atmos      = source.atmosphere || GOAL_ATMOSPHERE[source.category] || "an elegant setting";
  const finalCut   = source.cut       || "A-line";
  const finalNeck  = source.neckline  || "V-Neck";
  const finalSlv   = source.sleeves   || "Sleeveless";

  // ── Canonical garment anchor (mirrors Studio exactly) ──────────────────────
  // Inline colour description map — mirrors Studio's COLOUR_PALETTE imagenDesc values
  const SALON_COLOUR_DESC = {
    "Dusty Rose":"dusty rose, muted antique pink","Blush":"soft blush pink, delicate pale rose",
    "Rose":"soft rose pink, warm petal tone","Antique Rose":"antique rose, medium-depth muted warm pink",
    "Hibiscus":"hibiscus, deep warm rose pink","Mauve":"mauve, dusty muted purple-rose",
    "Lilac":"soft lilac, pale purple","Amethyst":"amethyst, rich medium purple","Plum":"deep plum, dark rich purple",
    "Coral":"coral, warm orange-red","Terracotta":"terracotta, warm earthy clay tone",
    "Brick":"brick red, deep warm red-brown","Scarlet":"scarlet, vivid warm red",
    "Burgundy":"deep wine, dark rose-red, bordeaux","Wine":"dark wine rose, deep merlot",
    "Garnet":"garnet, very deep dark red","Oxblood":"oxblood, extremely deep dark red-brown",
    "Champagne":"champagne, warm pale gold","Ivory":"ivory, warm white with yellow undertone",
    "Cream":"cream, soft warm off-white","Pearl":"pearl white, cool luminous white",
    "White":"pure white","Ecru":"ecru, natural warm off-white",
    "Midnight":"midnight black, deep rich black","Charcoal":"charcoal, dark grey",
    "Slate":"slate grey, cool medium grey","Silver":"silver, cool metallic grey",
    "Navy":"deep navy blue","Cobalt":"cobalt blue, vivid medium blue",
    "Dusty Blue":"dusty blue, muted soft blue","Ice Blue":"ice blue, very pale cool blue",
    "Sage":"sage green, soft muted green","Forest":"forest green, deep rich green",
    "Spruce":"spruce, dark cool green","Emerald":"emerald green, vivid jewel green",
    "Gold":"warm gold, rich metallic yellow","Bronze":"bronze, warm dark metallic",
    "Champagne Gold":"champagne gold, pale warm metallic",
  };
  const imagenColourDesc = SALON_COLOUR_DESC[source.colorDescription] || source.colorDescription || "neutral";
  const hexColour        = source.colorHex || "#c9a0a0";
  const fabricMeta       = FABRIC_META[source.fabricId] || FABRIC_META["custom"];
  const fabricName       = fabricMeta.name;
  const hemLogic         = DRESS_LENGTH_HEM_LOGIC[source.dressLength] || `${String(source.dressLength || "midi").toLowerCase()} length`;

  const garmentDesc = buildSalonCompositeGarmentDesc(source, fabricName, finalCut, finalNeck, finalSlv);

  const colourLock =
    `COLOUR: ${imagenColourDesc} (hex ${hexColour}) throughout the entire garment — ` +
    `do not shift lighter, darker, cooler, warmer, or more saturated. ` +
    `This exact hex must be matched in both front and back renders.`;

  // ── Design details ─────────────────────────────────────────────────────────
  const designDetails    = source.designDetails || {};
  const hasSurfaceDetail = designDetails.surface && designDetails.surface !== "None";
  const frontDetail      = source.frontDescription || "";
  const backDetail       = source.backDescription  || "";
  const hasAnyDetail     = hasSurfaceDetail ||
    (designDetails.bodice && designDetails.bodice !== "Clean") ||
    (designDetails.waist  && designDetails.waist  !== "Clean waist") ||
    (designDetails.skirtBehavior && designDetails.skirtBehavior !== "Clean fall") ||
    (designDetails.skirtFeature  && designDetails.skirtFeature  !== "None") ||
    frontDetail || backDetail;

  const surfaceBolster   = hasSurfaceDetail ? `VISIBLE ON GARMENT: ${designDetails.surface} — must be clearly rendered. ` : "";
  const detailSummary    = source.designDetailsSummary
    ? (surfaceBolster ? surfaceBolster + `Full design: ${source.designDetailsSummary}. ` : `Design details: ${source.designDetailsSummary}. `)
    : "";
  const frontDetailFinal = frontDetail
    ? `${frontDetail} ${detailSummary}`
    : detailSummary || (hasAnyDetail ? "" : `Plain ${fabricName} — no embellishment.`);
  const backDetailFinal  = backDetail
    ? `${backDetail} ${detailSummary}`
    : detailSummary || (hasAnyDetail ? "" : `Plain ${fabricName} back.`);

  // ── Locks ──────────────────────────────────────────────────────────────────
  const figureDescriptor = buildFigureDescriptor(measurements);
  const skinToneDesc     = skinTone ? ({
    "fair-cool":   "fair cool complexion, porcelain skin tone",
    "fair-warm":   "fair warm complexion, light golden skin tone",
    "medium-warm": "medium warm complexion, golden tan skin tone",
    "medium-cool": "medium cool complexion, olive skin tone",
    "deep-warm":   "deep warm complexion, rich brown skin tone",
    "deep-cool":   "deep cool complexion, deep brown skin tone",
  }[skinTone] || "") : "";

  const figureLock = figureDescriptor
    ? `The model is an adult woman with the following figure: ${figureDescriptor}.${skinToneDesc ? " Skin tone: " + skinToneDesc + "." : ""} Cast the model to match these proportions exactly — do not substitute a standard fashion model body. The model in the back view must have the same hair colour, hair length, build, and skin tone as the front view.`
    : `The model must be a real adult woman — healthy, natural proportions, not exaggerated thin.${skinToneDesc ? " Skin tone: " + skinToneDesc + "." : ""} The model in the back view must match the front view model exactly.`;

  const coreDesignLock =
    `Render this exact garment with no creative reinterpretation: ${garmentDesc}. ` +
    `Fabric: ${fabricName}. Keep the fabric visually faithful. ` +
    `${colourLock} ` +
    `Hem: strictly ${hemLogic}. Do not shorten or lengthen. ` +
    `Neckline: exactly ${finalNeck} at the front. Do not change. ` +
    `Back neckline: exactly ${source.backNeckline || "as designed"}. Do not change. ` +
    `Sleeves: exactly ${finalSlv}. Do not change. ` +
    (hasSurfaceDetail ? `REQUIRED SURFACE DETAIL — render this visibly: ${designDetails.surface}. ` : "") +
    (hasAnyDetail && (designDetails.skirtBehavior && designDetails.skirtBehavior !== "Clean fall" ||
      designDetails.waist && designDetails.waist !== "Clean waist" ||
      designDetails.bodice && designDetails.bodice !== "Clean")
      ? `STRUCTURAL REQUIREMENT — these construction details are mandatory: ${source.designDetailsSummary} `
      : "") +
    (hasAnyDetail
      ? "Do not add ANY embellishment beyond what is explicitly described. "
      : "Plain unembellished garment — no beading, embroidery, appliqué, lace, trim, or decoration.");

  const detailGuard = "Render only what is described. Do not add, invent or embellish any design element.";
  const sceneLock   = `Scene: ${atmos}.`;
  const singleShot  = "Render exactly one full-body model, centered, single frame, one camera angle only.";
  const style       = "Photorealistic studio-quality fashion image, realistic fabric texture, no text, no watermark.";

  const backFitLock =
    `The garment must fit the model cleanly from behind — show the full ${finalCut} silhouette head to hem. ` +
    `Hem must fall at ${hemLogic}. Do not crop the hem. ` +
    `Back neckline: ${source.backNeckline || finalNeck}. ` +
    `Closure: ${source.closure || "centre back"}. Render as a clean seam line if closure cannot be rendered precisely.`;

  const negativePrompt =
    "diptych, split-screen, collage, triptych, multi-panel, two models, multiple people, group photo, " +
    "mirrored duplicate, front and back in one frame, side-by-side, contact sheet, two poses, duplicate subject, " +
    "exaggerated thin model, skeleton, underweight, maternity, pregnant, baby bump";

  const frontPrompt = `${coreDesignLock} ${detailGuard} ${figureLock} ${sceneLock} ` +
    `FRONT VIEW ONLY: model faces camera, full body visible head to hem. ` +
    `Front details: ${frontDetailFinal}. ${singleShot} Do not show back angle or secondary pose. ${style}`;

  const backPrompt  = `${coreDesignLock} ${detailGuard} ${figureLock} ${sceneLock} ` +
    `BACK VIEW ONLY: model turned fully away from camera, full body visible head to hem. ` +
    `${backFitLock} Back details: ${backDetailFinal}. ${singleShot} Do not show front angle or secondary pose. ${style}`;

  return { frontPrompt, backPrompt, negativePrompt };
}

const severityStyle = (s) => {
  if (s === "positive") return { bg:"#f0f7f0", border:"#c8e0c8", dot:"#6aa36a" };
  if (s === "advisory") return { bg:"#fdf8f3", border:"#e8d8c4", dot:"#c8963c" };
  return { bg:"#fafaf9", border:"#f0eeec", dot:"#c4bfba" };
};

const S = {
  page: { margin:0, padding:0, minHeight:"100vh", backgroundColor:"#FDFCF9", color:C, fontFamily:"system-ui,sans-serif", fontSize:14, lineHeight:1.4 },
  hdr:  { margin:0, padding:"14px 24px", backgroundColor:"#fff", borderBottom:"1px solid #e7e5e4", display:"flex", alignItems:"center", position:"sticky", top:0, zIndex:50 },
  main: { margin:"0 auto", padding:"0 20px 64px", maxWidth:680, display:"flex", flexDirection:"column", gap:20 },
  card: { margin:0, padding:24, backgroundColor:"#fff", borderRadius:24, border:"1px solid #f0eeec", boxShadow:"0 1px 4px rgba(0,0,0,0.05)" },
  lbl:  { fontSize:9, fontWeight:700, letterSpacing:"0.18em", color:"#a8a29e", textTransform:"uppercase", fontFamily:"system-ui,sans-serif" },
};

function StepHeader({ onReturnToStudio }) {
  return (
    <div style={{ backgroundColor:"#fff", borderBottom:"1px solid #e7e5e4", padding:"22px 24px 20px", marginLeft:-20, marginRight:-20, marginBottom:4 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:6, minWidth:90 }}>
          <div style={{ width:18, height:18, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", backgroundColor:"#f0eeec", border:"1.5px solid #e0dbd4" }}>
            <CheckCircle2 style={{ width:10, height:10, color:"#c4bfba" }} />
          </div>
          <span style={{ fontSize:10, fontWeight:400, color:"#c4bfba", fontFamily:"system-ui,sans-serif" }}>Measurements</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:34, height:34, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", backgroundColor:"#C9A0A0", boxShadow:"0 2px 8px rgba(45,41,38,0.22)" }}>
            <span style={{ fontSize:14, fontWeight:700, color:"#2D2926", fontFamily:"system-ui,sans-serif", lineHeight:1 }}>3</span>
          </div>
          <span style={{ fontSize:17, fontWeight:700, color:C, fontFamily:"system-ui,sans-serif", letterSpacing:"-0.02em" }}>The Salon</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:6, minWidth:90, justifyContent:"flex-end" }}>
          <span style={{ fontSize:10, fontWeight:400, color:"#c4bfba", fontFamily:"system-ui,sans-serif" }}>The Drawing Room</span>
          <div style={{ width:18, height:18, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", backgroundColor:"#f5f3f0", border:"1.5px solid #e8e4e0" }}>
            <span style={{ fontSize:9, fontWeight:600, color:"#d4d0cc", fontFamily:"system-ui,sans-serif" }}>4</span>
          </div>
        </div>
      </div>
      <button onClick={onReturnToStudio} style={{ display:"flex", alignItems:"center", gap:7, padding:"8px 14px", borderRadius:999, backgroundColor:"#f5f4f2", border:"1px solid #e7e5e4", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>
        <ArrowLeft style={{ width:11, height:11, color:"#78716c" }} />
        <span style={{ fontSize:10, fontWeight:700, color:"#78716c", letterSpacing:"0.12em", textTransform:"uppercase" }}>Return to The Studio</span>
      </button>
    </div>
  );
}

// ─── Render download helper ───────────────────────────────────────────────────
function downloadRender(src, filename) {
  const a = document.createElement("a");
  a.href = src;
  a.download = filename + ".jpg";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

async function shareRender(renderImage, designLabel) {
  const shareData = {
    title: "My Tabitha Studio Design",
    text: "I designed this dress with Tabitha Studio — " + designLabel + ". Bespoke dressmaking, made accessible.",
    url: window.location.href,
  };
  // On mobile/supported browsers, try to share the image file directly
  if (navigator.canShare && renderImage && renderImage.startsWith("data:")) {
    try {
      const res  = await fetch(renderImage);
      const blob = await res.blob();
      const file = new File([blob], "My-Tabitha-Design.jpg", { type: "image/jpeg" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ ...shareData, files: [file] });
        return;
      }
    } catch (_) {}
  }
  // Fallback: native share without file, or clipboard copy
  if (navigator.share) {
    try { await navigator.share(shareData); return; } catch (_) {}
  }
  try {
    await navigator.clipboard.writeText(window.location.href);
    alert("Link copied to clipboard — paste it wherever you'd like to share.");
  } catch (_) {}
}

function RenderZone({ form, measurements, renderImage, renderImageBack, isGenerating, onGenerate }) {
  const fabric = FABRIC_META[form.fabricId] || FABRIC_META["custom"];
  const label  = fabric.name + " \u00b7 " + (form.separates ? (form.skirtType + " Separates") : (form.cut || "A-line"));
  const hasImages = renderImage || renderImageBack;
  const designLabel = (form.colorDescription || "") + " " + (form.cut || "A-line") + " in " + fabric.name;

  return (
    <div style={{ ...S.card, padding:0, overflow:"hidden" }}>
      <div style={{ backgroundColor:"#1a1714", position:"relative" }}>
        {hasImages ? (
          <div style={{ display:"flex", flexDirection:"row", gap:2, width:"100%" }}>
            {[{ src:renderImage, label:"Front View" }, { src:renderImageBack, label:"Back View" }].map(function(view) {
              return (
                <div key={view.label} style={{ flex:1, position:"relative", backgroundColor:"#1a1714" }}>
                  {view.src
                    ? <img src={view.src} alt={view.label} style={{ display:"block", width:"100%", height:"auto" }} />
                    : <div style={{ minHeight:480, display:"flex", alignItems:"center", justifyContent:"center", opacity:0.15 }}>
                        <Sparkles style={{ width:24, height:24, color:"#fff" }} />
                      </div>
                  }
                  <div style={{ position:"absolute", bottom:10, left:10, backgroundColor:"rgba(255,255,255,0.88)", backdropFilter:"blur(6px)", padding:"4px 10px", borderRadius:999, fontSize:8, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", color:C }}>
                    {view.label}
                  </div>
                </div>
              );
            })}
          </div>
        ) : isGenerating ? (
          <div style={{ minHeight:480, display:"flex", alignItems:"center", justifyContent:"center", textAlign:"center", padding:48 }}>
            <div style={{ width:48, height:48, margin:"0 auto 20px", position:"relative", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <div style={{ position:"absolute", inset:0, borderRadius:"50%", border:"2px solid rgba(201,160,160,0.15)" }} />
              <div style={{ position:"absolute", inset:0, borderRadius:"50%", border:"2px solid transparent", borderTopColor:"#C9A0A0", animation:"spin 1s linear infinite" }} />
              <Sparkles style={{ width:18, height:18, color:"rgba(201,160,160,0.6)" }} />
            </div>
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:"0.2em", color:"rgba(255,255,255,0.5)", textTransform:"uppercase", fontFamily:"system-ui,sans-serif", marginBottom:8 }}>Creating your render</div>
            <div style={{ fontSize:10, color:"rgba(255,255,255,0.25)", fontFamily:"system-ui,sans-serif", lineHeight:1.6 }}>Usually ready in 30–60 seconds.<br/>Please don't refresh.</div>
          </div>
        ) : (
          <div style={{ minHeight:480, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center", padding:40, opacity:0.2 }}>
            <Sparkles style={{ width:36, height:36, color:"#fff", margin:"0 auto 12px", display:"block" }} />
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.25em", color:"#fff", textTransform:"uppercase", fontFamily:"system-ui,sans-serif" }}>Your render will appear here</div>
          </div>
        )}
        {isGenerating && hasImages && (
          <div style={{ position:"absolute", inset:0, backgroundColor:"rgba(26,23,20,0.75)", backdropFilter:"blur(4px)", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <div style={{ textAlign:"center" }}>
              <div style={{ width:44, height:44, margin:"0 auto 16px", position:"relative", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <div style={{ position:"absolute", inset:0, borderRadius:"50%", border:"2px solid rgba(201,160,160,0.15)" }} />
                <div style={{ position:"absolute", inset:0, borderRadius:"50%", border:"2px solid transparent", borderTopColor:"#C9A0A0", animation:"spin 1s linear infinite" }} />
                <Sparkles style={{ width:16, height:16, color:"rgba(201,160,160,0.7)" }} />
              </div>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.2em", color:"#fff", textTransform:"uppercase", fontFamily:"system-ui,sans-serif", marginBottom:6 }}>Creating your render</div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)", fontFamily:"system-ui,sans-serif" }}>Please don't refresh</div>
            </div>
          </div>
        )}
        {hasImages && !isGenerating && (
          <div style={{ position:"absolute", bottom:22, right:22, backgroundColor:"rgba(255,255,255,0.9)", backdropFilter:"blur(8px)", padding:"5px 12px", borderRadius:999, fontSize:9, fontWeight:700, letterSpacing:"0.15em", textTransform:"uppercase", color:C, fontFamily:"system-ui,sans-serif" }}>
            {label}
          </div>
        )}
      </div>
      <div style={{ padding:"16px 24px", borderTop:"1px solid #f0eeec", display:"flex", alignItems:"center", justifyContent:"space-between", gap:14 }}>
        <div style={{ fontSize:11, color:"#a8a29e", fontStyle:"italic", fontFamily:"system-ui,sans-serif", lineHeight:1.5, flex:1 }}>
          {hasImages
            ? "These renders are personalised to your measurements and design choices."
            : "Generate front and back renders personalised to your measurements, silhouette, fabric and colour."}
        </div>
        <button onClick={onGenerate} disabled={isGenerating} style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0, padding:"12px 20px", backgroundColor:isGenerating ? "#e7e5e4" : "#C9A0A0", color:isGenerating ? "#b8b0a8" : "#2D2926", fontWeight:700, fontSize:10, letterSpacing:"0.25em", textTransform:"uppercase", borderRadius:12, border:"none", cursor:isGenerating ? "not-allowed" : "pointer", boxShadow:isGenerating ? "none" : "0 3px 14px rgba(45,41,38,0.2)", fontFamily:"system-ui,sans-serif", transition:"all 0.2s ease" }}>
          {isGenerating ? <Loader2 style={{ width:13, height:13, animation:"spin 1s linear infinite" }} /> : <Sparkles style={{ width:13, height:13, color:"#2D2926" }} />}
          {hasImages ? "Regenerate" : "Generate Render"}
        </button>
      </div>

      {/* ── Share / save bar — shown when renders exist ── */}
      {hasImages && !isGenerating && (
        <div style={{ padding:"12px 20px", borderTop:"1px solid #f5f3f0", backgroundColor:"#fafaf9", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
          <span style={{ fontSize:9, fontWeight:700, letterSpacing:"0.18em", color:"#a8a29e", textTransform:"uppercase", fontFamily:"system-ui,sans-serif", marginRight:4 }}>Save or share</span>
          {renderImage && (
            <button onClick={function() { downloadRender(renderImage, "Tabitha-Front-View"); }} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:999, border:"1px solid #E0D9D3", backgroundColor:"#fff", cursor:"pointer", fontSize:10, fontWeight:700, color:C, letterSpacing:"0.1em", fontFamily:"system-ui,sans-serif" }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Front
            </button>
          )}
          {renderImageBack && (
            <button onClick={function() { downloadRender(renderImageBack, "Tabitha-Back-View"); }} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:999, border:"1px solid #E0D9D3", backgroundColor:"#fff", cursor:"pointer", fontSize:10, fontWeight:700, color:C, letterSpacing:"0.1em", fontFamily:"system-ui,sans-serif" }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Back
            </button>
          )}
          {/* Instagram — native share on mobile (handles routing to Instagram), download fallback on desktop */}
          <button onClick={async function() {
            const src = renderImage;
            if (!src) return;
            // Step 1: download image to camera roll / downloads
            const a = document.createElement("a");
            a.href = src;
            a.download = "Tabitha-Design.jpg";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            // Step 2: open Instagram app (mobile) or instagram.com (desktop)
            setTimeout(function() {
              const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
              window.open(isMobile ? "instagram://" : "https://www.instagram.com", "_blank");
            }, 400);
          }} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:999, border:"1px solid #E0D9D3", backgroundColor:"#fff", cursor:"pointer", fontSize:10, fontWeight:700, color:C, letterSpacing:"0.1em", fontFamily:"system-ui,sans-serif" }}>
            {/* Instagram icon */}
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
            Instagram
          </button>
          {/* Pinterest — opens pin creator with current page URL */}
          <button onClick={function() {
            const url = encodeURIComponent(window.location.href);
            const desc = encodeURIComponent("I designed this " + designLabel + " with Tabitha Studio — bespoke dressmaking made accessible.");
            window.open("https://pinterest.com/pin/create/button/?url=" + url + "&description=" + desc, "_blank");
          }} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:999, border:"1px solid #E0D9D3", backgroundColor:"#fff", cursor:"pointer", fontSize:10, fontWeight:700, color:C, letterSpacing:"0.1em", fontFamily:"system-ui,sans-serif" }}>
            {/* Pinterest icon */}
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>
            Pinterest
          </button>
          <button onClick={function() { shareRender(renderImage, designLabel); }} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 16px", borderRadius:999, border:"none", backgroundColor:C, cursor:"pointer", fontSize:10, fontWeight:700, color:"#fff", letterSpacing:"0.1em", fontFamily:"system-ui,sans-serif" }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
            Share
          </button>
        </div>
      )}
    </div>
  );
}

function FitObservations({ observations, onDiscuss }) {
  if (!observations.length) return null;
  return (
    <div style={S.card}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:18 }}>
        <Info style={{ width:13, height:13, color:"#a8a29e" }} />
        <span style={S.lbl}>Fit Observations</span>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {observations.map((ob, i) => {
          const sv = severityStyle(ob.severity);
          return (
            <div key={i} style={{ padding:"14px 16px", borderRadius:14, backgroundColor:sv.bg, border:"1px solid " + sv.border }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                  <div style={{ width:6, height:6, borderRadius:"50%", backgroundColor:sv.dot, flexShrink:0 }} />
                  <span style={{ fontSize:10, fontWeight:700, color:C, letterSpacing:"0.08em", fontFamily:"system-ui,sans-serif" }}>{ob.signal}</span>
                </div>
                <button onClick={() => onDiscuss(ob)} style={{ display:"flex", alignItems:"center", gap:4, padding:"3px 10px", borderRadius:999, backgroundColor:"rgba(45,41,38,0.06)", border:"none", cursor:"pointer", fontSize:9, fontWeight:700, color:"#78716c", letterSpacing:"0.12em", textTransform:"uppercase", fontFamily:"system-ui,sans-serif" }}>
                  Discuss <ChevronRight style={{ width:9, height:9 }} />
                </button>
              </div>
              <p style={{ margin:0, fontSize:11, color:"#57534e", lineHeight:1.6, fontFamily:"system-ui,sans-serif", marginBottom:5 }}>{ob.observation}</p>
              <p style={{ margin:0, fontSize:11, color:"#78716c", lineHeight:1.6, fontStyle:"italic", fontFamily:"system-ui,sans-serif" }}>{ob.technique}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AdjustmentChat({ form, measurements, observations }) {
  const fabricName = (FABRIC_META[form.fabricId] || {}).name || "your chosen fabric";
  const silhouette = form.cut || "your chosen silhouette";
  const occasionLabel = { business:"business", cocktail:"cocktail", gala:"gala", bridal:"bridal" }[form.category];
  const occasionPhrase = occasionLabel ? "a " + occasionLabel + " occasion" : "your occasion";

  const [messages, setMessages] = useState([{
    role: "assistant",
    content: "Welcome to the Salon. You're working with a " + silhouette + " in " + fabricName + " for " + occasionPhrase + ". I'm here to talk through the design — silhouette, fabric behaviour, construction details, or anything you'd like to refine.",
  }]);
  const [input,   setInput]   = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior:"smooth" });
    }
  }, [messages]);

  const systemPrompt = useMemo(() => {
    const lengthLabel = form.dressLength || "midi";
    const colorLabel  = form.colorDescription || "unspecified";
    const neckline    = form.neckline  || "unspecified";
    const sleeves     = form.sleeves   || "unspecified";
    const lining      = form.lining    || "unspecified";
    const hollow      = measurements.hollowToHem || null;

    return [
      "You are the design advisor for Hem and Haw, a semi-luxury bespoke atelier platform.",
      "Your role is to help the client refine and understand their design — not to assess or comment on their body.",
      "",
      "Current design specification:",
      "- Silhouette: " + silhouette,
      "- Fabric: " + fabricName,
      "- Neckline: " + neckline,
      "- Sleeves: " + sleeves,
      "- Length: " + lengthLabel,
      "- Colour: " + colorLabel,
      "- Lining: " + lining,
      "- Occasion: " + occasionPhrase,
      hollow ? "- Hollow-to-hem measurement: " + hollow + " inches (use only for garment length calculations, never as body commentary)" : "",
      "",
      "Design philosophy:",
      "- Speak entirely in terms of garment design, fabric behaviour, and construction technique.",
      "- Never reference the client's body, proportions, shape, or measurements as subjects.",
      "- Frame everything as a property of the garment: 'a bias cut in this fabric will...' not 'your figure would benefit from...'",
      "- Preferred techniques: bias cut, cowl drape, wrap front, French dart, princess seaming, ease pleats, petersham ribbon waist stay, self-fabric interior ties.",
      "- Construction notes should feel like advice from a knowledgeable dressmaker, not a fitting critique.",
      "- If the client raises fit concerns, translate them immediately into design solutions.",
      "",
      "Respond in 2 to 4 sentences. Be specific, warm, and actionable. Never use the words 'flattering', 'slimming', 'figure', 'proportion', 'body type', or any phrase that evaluates the client's form.",
    ].filter(Boolean).join("\n");
  }, [form, measurements, observations, fabricName, silhouette, occasionPhrase]);

  const send = useCallback(async function(text) {
    if (!text.trim() || loading) return;
    const userMsg = { role:"user", content:text };
    setMessages(function(prev) { return [...prev, userMsg]; });
    setInput("");
    setLoading(true);
    try {
      const history = [...messages, userMsg];
      const data = await callAnthropicMessages({
        model: "claude-sonnet-4-6",
        max_tokens: 1000,
        system: systemPrompt,
        messages: history,
      });
      const reply = (data.content || []).map(function(b) { return b.text || ""; }).join("") || "Something went wrong — please try again.";
      setMessages(function(prev) { return [...prev, { role:"assistant", content:reply }]; });
    } catch (e) {
      setMessages(function(prev) { return [...prev, { role:"assistant", content:"Something went wrong — please try again." }]; });
    } finally {
      setLoading(false);
    }
  }, [messages, loading, systemPrompt]);

  return (
    <div style={S.card}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:18 }}>
        <MessageCircle style={{ width:13, height:13, color:"#a8a29e" }} />
        <span style={S.lbl}>Adjustment Conversation</span>
      </div>
      <div style={{ maxHeight:320, overflowY:"auto", display:"flex", flexDirection:"column", gap:10, marginBottom:14 }}>
        {messages.map(function(msg, i) {
          const isUser = msg.role === "user";
          return (
            <div key={i} style={{ display:"flex", justifyContent:isUser ? "flex-end" : "flex-start" }}>
              <div style={{ maxWidth:"85%", padding:"10px 14px", borderRadius:isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px", backgroundColor:isUser ? C : "#f5f4f2", border:isUser ? "none" : "1px solid #f0eeec" }}>
                <p style={{ margin:0, fontSize:12, lineHeight:1.65, color:isUser ? "#fff" : "#3c3633", fontFamily:"system-ui,sans-serif" }}>{msg.content}</p>
              </div>
            </div>
          );
        })}
        {loading && (
          <div style={{ display:"flex", justifyContent:"flex-start" }}>
            <div style={{ padding:"10px 16px", borderRadius:"16px 16px 16px 4px", backgroundColor:"#f5f4f2", border:"1px solid #f0eeec" }}>
              <Loader2 style={{ width:13, height:13, color:"#a8a29e", animation:"spin 1s linear infinite" }} />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:12, marginTop:4 }}>
        {PROMPT_CHIPS.map(function(chip, i) {
          return (
            <button key={i} onClick={function() { send(chip.message); }} disabled={loading} style={{ padding:"6px 14px", borderRadius:999, backgroundColor:"#f5f4f2", border:"1px solid #e7e5e4", fontSize:11, fontWeight:600, color:"#57534e", cursor:loading ? "not-allowed" : "pointer", fontFamily:"system-ui,sans-serif", opacity:loading ? 0.5 : 1 }}>
              {chip.label}
            </button>
          );
        })}
      </div>
      <div style={{ display:"flex", gap:10, alignItems:"flex-end" }}>
        <textarea
          value={input}
          onChange={function(e) { setInput(e.target.value); }}
          onKeyDown={function(e) { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
          placeholder="Ask anything about your design, fit, or fabric..."
          rows={2}
          style={{ flex:1, padding:"10px 14px", backgroundColor:"#fafaf9", borderRadius:12, border:"1px solid #e7e5e4", fontSize:12, outline:"none", fontFamily:"system-ui,sans-serif", color:"#1c1917", resize:"none", lineHeight:1.5 }}
        />
        <button onClick={function() { send(input); }} disabled={!input.trim() || loading} style={{ width:42, height:42, borderRadius:12, flexShrink:0, backgroundColor:input.trim() && !loading ? "#C9A0A0" : "#e7e5e4", border:"none", cursor:input.trim() && !loading ? "pointer" : "not-allowed", display:"flex", alignItems:"center", justifyContent:"center", transition:"background-color 0.2s ease" }}>
          <Send style={{ width:15, height:15, color:input.trim() && !loading ? "#2D2926" : "#b8b0a8" }} />
        </button>
      </div>
    </div>
  );
}

export default function Salon({
  form                       = {},
  designSpec                 = null,
  measurements               = {},
  fabricYardage               = null,
  costEstimate                = null,
  designRender                = null,
  designRenderBack            = null,
  designSeed                  = null,
  initialFittingRoomRender    = null,
  initialFittingRoomRenderBack = null,
  initialFittingRoomAdjustments = [],
  initialDesignBrief          = "",
  onFittingRoomDataChange     = function() {},
  onReturnToFittingRoom       = function() {},
  onReturnToStudio            = function() {},
  onProceedToDrawingRoom      = function() {},
  skinTone                    = '',
  isPreviewMeasurements       = false,
}) {
  const [renderImage,     setRenderImage]     = useState(initialFittingRoomRender);
  const [renderImageBack, setRenderImageBack] = useState(initialFittingRoomRenderBack);
  const [isGenerating,    setIsGenerating]    = useState(false);

  const chatRef = useRef(null);
  const effectiveForm = useMemo(function() {
    return mergeFormWithDesignSpec(form, designSpec);
  }, [form, designSpec]);

  const observations = useMemo(function() {
    return analyseProportions(measurements, effectiveForm.cut || "A-line", effectiveForm.fabricId || "silk");
  }, [measurements, effectiveForm.cut, effectiveForm.fabricId]);

  const handleGenerate = useCallback(async function() {
    setIsGenerating(true);
    const { frontPrompt, backPrompt, negativePrompt } = buildSalonPrompts(effectiveForm, measurements, designSpec, skinTone);
    const renderOptions = designSeed ? { negativePrompt, seed: designSeed } : { negativePrompt };
    const [frontResult, backResult] = await Promise.allSettled([
      callImagenWithRetry({ prompt: frontPrompt, ...renderOptions }),
      callImagenWithRetry({ prompt: backPrompt,  ...renderOptions }),
    ]);
    if (frontResult.status === "fulfilled") {
      setRenderImage(frontResult.value);
    } else {
      console.error("Imagen front render failed:", frontResult.reason);
      setRenderImage("PLACEHOLDER");
    }
    if (backResult.status === "fulfilled") {
      setRenderImageBack(backResult.value);
    } else {
      console.error("Imagen back render failed, retrying once:", backResult.reason);
      try {
        const backRetry = await callImagenWithRetry({ prompt: backPrompt, ...renderOptions });
        setRenderImageBack(backRetry);
      } catch (e2) {
        console.error("Imagen back render retry failed:", e2);
        setRenderImageBack("PLACEHOLDER");
      }
    }
    setIsGenerating(false);
  }, [effectiveForm, measurements, designSpec, designSeed]);

  const handleDiscuss = useCallback(function() {
    if (chatRef.current) {
      chatRef.current.scrollIntoView({ behavior:"smooth" });
    }
  }, []);

  useEffect(function() {
    onFittingRoomDataChange({
      fittingRoomRender:     renderImage     === "PLACEHOLDER" ? null : renderImage,
      fittingRoomRenderBack: renderImageBack === "PLACEHOLDER" ? null : renderImageBack,
    });
  }, [renderImage, renderImageBack, onFittingRoomDataChange]);

  const handleProceedToDrawingRoom = useCallback(function() {
    onProceedToDrawingRoom({
      fittingRoomRender:     renderImage     === "PLACEHOLDER" ? null : renderImage,
      fittingRoomRenderBack: renderImageBack === "PLACEHOLDER" ? null : renderImageBack,
    });
  }, [renderImage, renderImageBack, onProceedToDrawingRoom]);

  const hasMeasurements = Object.keys(measurements).some(function(k) { return !!measurements[k]; });

  return (
    <div style={S.page}>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        button:hover { opacity: 0.85; transition: opacity 0.15s ease; }
        textarea::placeholder { font-style: italic; color: #b8b0a8; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #e0dbd4; border-radius: 999px; }
      `}</style>

      <div style={S.hdr}>
        <Layers style={{ color:C, marginRight:10, width:20, height:20, flexShrink:0 }} />
        <div style={{ flex:1 }}>
          <div style={{ fontSize:17, fontWeight:600, letterSpacing:"-0.02em", fontFamily:"system-ui,sans-serif" }}>Tabitha</div>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.3em", color:"#a8a29e", textTransform:"uppercase", fontFamily:"system-ui,sans-serif" }}>The Salon</div>
        </div>
        <button onClick={onReturnToStudio} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:999, backgroundColor:"#f5f4f2", border:"1px solid #e7e5e4", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>
          <ArrowLeft style={{ width:11, height:11, color:"#78716c" }} />
          <span style={{ fontSize:10, fontWeight:700, color:"#78716c", letterSpacing:"0.12em", textTransform:"uppercase" }}>Return to The Studio</span>
        </button>
      </div>

      <div style={{ maxWidth:680, margin:"0 auto", padding:"0 20px" }}>
        <StepHeader onReturnToStudio={onReturnToStudio} />
      </div>

      <div style={S.main}>

        <RenderZone
          form={effectiveForm}
          measurements={measurements}
          renderImage={renderImage === "PLACEHOLDER" ? null : renderImage}
          renderImageBack={renderImageBack === "PLACEHOLDER" ? null : renderImageBack}
          isGenerating={isGenerating}
          onGenerate={handleGenerate}
        />

        {hasMeasurements && (
          <FitObservations observations={observations} onDiscuss={handleDiscuss} />
        )}

        <div ref={chatRef}>
          <AdjustmentChat form={effectiveForm} measurements={measurements} observations={observations} />
        </div>

        <button onClick={handleProceedToDrawingRoom} style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, width:"100%", padding:"18px 0", backgroundColor:"#C9A0A0", color:"#2D2926", fontWeight:700, fontSize:11, letterSpacing:"0.35em", textTransform:"uppercase", borderRadius:14, border:"none", cursor:"pointer", boxShadow:"0 4px 20px rgba(45,41,38,0.22)", fontFamily:"system-ui,sans-serif" }}>
          Proceed to The Drawing Room
          <ChevronRight style={{ width:15, height:15, color:"#2D2926" }} />
        </button>

        <div style={{ textAlign:"center", fontSize:9, color:"#b8b0a8", letterSpacing:"0.15em", paddingTop:4, fontFamily:"system-ui,sans-serif" }}>
          &copy; Tabitha 2026
        </div>

      </div>
    </div>
  );
}
